import React from 'react';
import { Box, Container, List, ListItem, ListItemText, Paper, Typography } from '@mui/material';
import withWebsiteContent from '../../../common/withWebsiteContent';

/**
 * Terms and conditions — content from website-content defaultProps.
 *
 * Expected defaultProps shape (terms-and-conditions/metadata.json):
 * {
 *   "title": "...",
 *   "leadTitle": "...",
 *   "leadParagraph": "...",
 *   "sections": [{ "title": "...", "items": ["...", "..."] }],
 *   "definitionsTitle": "...",
 *   "definitions": ["...", "..."]
 * }
 */
export const TermsAndConditionsPure = ({
  title = '',
  leadTitle = '',
  leadParagraph = '',
  sections = [],
  definitionsTitle = '',
  definitions = [],
  images = [],
}) => {
  const hasContent =
    title ||
    leadTitle ||
    leadParagraph ||
    sections.length > 0 ||
    definitions.length > 0;

  if (!hasContent) return null;

  return (
    <Box sx={{ py: 6 }}>
      <Container maxWidth="md">
        {title && (
          <Typography
            variant="h4"
            component="h1"
            sx={{ textAlign: 'center', mb: 4, fontWeight: 600 }}
          >
            {title}
          </Typography>
        )}
        <Paper variant="outlined" sx={{ p: { xs: 3, md: 4 }, borderRadius: 2, borderColor: 'divider' }}>
          {leadTitle && (
            <Typography variant="h6" component="p" sx={{ fontWeight: 700, mb: 2 }}>
              {leadTitle}
            </Typography>
          )}
          {leadParagraph && (
            <Typography variant="body1" paragraph sx={{ whiteSpace: 'pre-line' }}>
              {leadParagraph}
            </Typography>
          )}

          {sections.map((section, sectionIndex) => (
            <Box key={section.title || sectionIndex} sx={{ mt: sectionIndex === 0 && !leadParagraph ? 0 : 3 }}>
              {section.title && (
                <Typography variant="h6" component="h2" sx={{ fontWeight: 700, mb: 1.5 }}>
                  {section.title}
                </Typography>
              )}
              {Array.isArray(section.items) && section.items.length > 0 && (
                <List dense disablePadding component="ol" sx={{ pl: 2, listStyle: 'decimal' }}>
                  {section.items.map((item, itemIndex) => (
                    <ListItem
                      key={itemIndex}
                      disableGutters
                      component="li"
                      sx={{ display: 'list-item', py: 0.5 }}
                    >
                      <ListItemText
                        primary={item}
                        primaryTypographyProps={{
                          variant: 'body1',
                          sx: { whiteSpace: 'pre-line' },
                        }}
                      />
                    </ListItem>
                  ))}
                </List>
              )}
            </Box>
          ))}

          {definitions.length > 0 && (
            <Box sx={{ mt: 4 }}>
              {definitionsTitle && (
                <Typography variant="h6" component="h2" sx={{ fontWeight: 700, mb: 1.5 }}>
                  {definitionsTitle}
                </Typography>
              )}
              <List dense disablePadding component="ol" sx={{ pl: 2, listStyle: 'decimal' }}>
                {definitions.map((item, index) => (
                  <ListItem
                    key={index}
                    disableGutters
                    component="li"
                    sx={{ display: 'list-item', py: 0.5 }}
                  >
                    <ListItemText
                      primary={item}
                      primaryTypographyProps={{
                        variant: 'body1',
                        sx: { whiteSpace: 'pre-line' },
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
        </Paper>
      </Container>
    </Box>
  );
};

const TermsAndConditions = withWebsiteContent(TermsAndConditionsPure);
TermsAndConditions.displayName = 'TermsAndConditions';

export default TermsAndConditions;
